import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";
import { LoadingSpinner } from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";
import { getMinMaxDates } from "../fiscalYearHelper";
import ShareTransferContextPanel, {
  getShareMaxTransferable,
} from "../ShareTransferContextPanel";

const CustomInput = ({ label, name, value, onChange, error, readOnly, disabled, type = "text", children }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      className="no-drag"
      readOnly={readOnly}
      disabled={disabled}
    />
    {children}
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const EditHalqaShare = ({ setModal, transactionData, unitId, onSuccess }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId")) || unitId;

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [shareContext, setShareContext] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const calculatedAmount = Number(shareContext?.minimumShare) || 0;
  const sourceAccountType =
    transactionData?.accountTypeCode === "amanat_account" ? "amanat" : "cash";

  const [formData, setFormData] = useState({
    share_month: new Date(),
    processed_date: new Date(),
    amount: "",
    memo: "",
  });

  const [errors, setErrors] = useState({});

  const parseMonthString = (monthStr) => {
    if (!monthStr) return null;
    const parts = monthStr.split(" ");
    if (parts.length === 2) {
      const monthIndex = new Date(`${parts[0]} 1, 2000`).getMonth();
      const year = parseInt(parts[1], 10);
      if (!isNaN(monthIndex) && !isNaN(year)) {
        return new Date(year, monthIndex, 1);
      }
    }
    return null;
  };

  const getShareMonthBounds = (shareMonthDate) => {
    const minDate = new Date(shareMonthDate.getFullYear(), shareMonthDate.getMonth(), 1);
    const maxDate = new Date(shareMonthDate.getFullYear(), shareMonthDate.getMonth() + 1, 0);
    return { minDate, maxDate };
  };

  const { minDate, maxDate } = getShareMonthBounds(formData.share_month || new Date());
  const { minDate: fiscalMinDate, maxDate: latestShareMonthMaxDate } = getMinMaxDates();

  useEffect(() => {
    if (transactionData) {
      let shareMonthDate = parseMonthString(transactionData.shareMonth || transactionData.share_month);

      if (!shareMonthDate && transactionData.memo) {
        const months = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ];
        const memoStr = transactionData.memo;
        const matchedMonth = months.find((m) => memoStr.toLowerCase().includes(m.toLowerCase()));
        const yearMatch = memoStr.match(/\b(20\d\d)\b/);
        if (matchedMonth && yearMatch) {
          const mIdx = months.indexOf(matchedMonth);
          shareMonthDate = new Date(parseInt(yearMatch[1], 10), mIdx, 1);
        }
      }

      if (!shareMonthDate) {
        shareMonthDate = transactionData.date ? new Date(transactionData.date) : new Date();
      }

      const parsedProcessedDate = transactionData.date ? new Date(transactionData.date) : new Date();

      setFormData({
        share_month: isNaN(shareMonthDate.getTime()) ? new Date() : shareMonthDate,
        processed_date: isNaN(parsedProcessedDate.getTime()) ? new Date() : parsedProcessedDate,
        amount: transactionData.amount ? Math.abs(transactionData.amount).toString() : "",
        memo: transactionData.memo || "",
      });
    }
  }, [transactionData]);

  const formatLocalDateString = (d) => {
    if (!d) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!formData.share_month || !loggedInUnitId) return;

    setCalcLoading(true);
    const formattedDate = formatLocalDateString(formData.share_month);

    ApiFinanceLocal()
      .GetHalqaShareContext(loggedInUnitId, formattedDate)
      .then((res) => {
        setShareContext(res.data || {});
        setCalcLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setCalcLoading(false);
      });
  }, [formData.share_month, loggedInUnitId]);

  const toggle = () => setModal(false);

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleShareMonthChange = (date) => {
    const selectedShareMonth = date ?? new Date();
    const { minDate } = getShareMonthBounds(selectedShareMonth);

    let nextProcessedDate = formData.processed_date || new Date();
    if (nextProcessedDate < minDate) {
      nextProcessedDate = minDate;
    }

    setFormData((prev) => ({
      ...prev,
      share_month: selectedShareMonth,
      processed_date: nextProcessedDate,
    }));
    setErrors((prev) => ({ ...prev, share_month: "", processed_date: "" }));
  };

  const validate = () => {
    const temp = {};
    let isValid = true;

    if (!formData.share_month) {
      temp.share_month = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.processed_date) {
      temp.processed_date = t("common_field_required_error");
      isValid = false;
    }

    if (!formData.amount || isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      temp.amount = t("common_field_required_error");
      isValid = false;
    } else {
      const parsedAmount = parseFloat(formData.amount);
      const maxTransferable = getShareMaxTransferable(shareContext, sourceAccountType);
      const totalInfaq = Number(shareContext?.totalInfaq) || 0;
      if (parsedAmount > totalInfaq) {
        temp.amount = t("share_amount_exceeds_total_infaq");
        isValid = false;
      } else if (parsedAmount > maxTransferable) {
        temp.amount = t("share_amount_exceeds_account_balance");
        isValid = false;
      }
    }

    if (!formData.memo || !formData.memo.trim()) {
      temp.memo = t("common_field_required_error");
      isValid = false;
    }

    setErrors(temp);
    return isValid;
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!validate()) {
      return;
    }

    const executeSubmit = () => {
      let finalMemo = formData.memo ? formData.memo.trim() : "";
      if (formData.share_month) {
        const monthName = formData.share_month.toLocaleString("en-US", { month: "long" });
        const year = formData.share_month.getFullYear();
        if (!finalMemo.toLowerCase().includes(monthName.toLowerCase())) {
          finalMemo = finalMemo ? `${finalMemo} (${monthName} ${year})` : `${monthName} ${year}`;
        }
      }

      const payload = {
        transactionNo: transactionData.transactionNo || transactionData.fundId || 0,
        date: formatLocalDateString(formData.processed_date),
        share_month: formatLocalDateString(formData.share_month),
        amount: parseFloat(formData.amount),
        memo: finalMemo,
        unitId: loggedInUnitId,
        isRequest: true,
        source_account_type: transactionData.accountTypeCode === "amanat_account" ? "amanat" : "cash",
        original: {
          transactionNo: transactionData.transactionNo || transactionData.fundId || 0,
          codeId: transactionData.codeId || transactionData.code_id || transactionData.category?.value || transactionData.categoryCode || "",
          amount: String(transactionData.amount ? Math.abs(transactionData.amount) : ""),
          date: formatLocalDateString(transactionData.date ? new Date(transactionData.date) : new Date()),
          memo: transactionData.memo || "",
        },
      };

      setLoading(true);

      ApiFinanceLocal()
        .RequestEditHalqaShare(payload)
        .then((res) => {
          const data = res.data;
          if (data?.statusCode === 200) {
            let msgKey = data?.message || "finance_halqa_share_edit_requested";
            let msgText = t(msgKey);
            if (msgText === msgKey || !msgText) {
              msgText = "Halqa Share edit request submitted for approval.";
            }
            swal({
              title: t("common_success"),
              text: msgText,
              icon: "success",
              buttons: "OK",
            });
            setModal(false);
            if (onSuccess) onSuccess();
          } else {
            let errText = data?.message || "common_something_went_wrong";
            if (t(errText) !== errText) errText = t(errText);
            const isWarning = data?.statusCode === 300 || data?.message === "modification_request_already_pending";
            swal({
              title: isWarning ? t("common_warning") || "Warning" : t("common_error"),
              text: errText,
              icon: isWarning ? "warning" : "error",
              buttons: "OK",
            }).then(() => {
              if (isWarning) {
                setModal(false);
              }
            });
          }
        })
        .catch((err) => {
          let rawErr = err?.response?.data?.message || err?.response?.data || err?.message || "common_something_went_wrong";
          if (typeof rawErr !== "string") rawErr = JSON.stringify(rawErr);
          if (t(rawErr) !== rawErr) rawErr = t(rawErr);
          swal({
            title: t("common_error"),
            text: rawErr,
            icon: "error",
            buttons: "OK",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    };

    executeSubmit();
  };

  return (
    <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
      <Modal isOpen className="inmodal" size="lg" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="edit_halqa_share" />
        </ModalHeader>

        {loading ? (
          <ModalBody className="text-center">
            <LoadingSpinner />
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <ModalBody>
              <ShareTransferContextPanel
                context={shareContext}
                loading={calcLoading}
                minimumLabelKey="share_minimum_halqa"
              />
              <Row className="mb-3">
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_share_month" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Select month"
                      className="form-control no-drag"
                      selected={formData.share_month}
                      onChange={handleShareMonthChange}
                      showMonthYearPicker
                      minDate={fiscalMinDate}
                      maxDate={latestShareMonthMaxDate}
                      dateFormat="MMMM yyyy"
                      disabled={true}
                    />
                    {errors.share_month && <h4 className="text-error">{errors.share_month}</h4>}
                  </FormGroup>
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_processed_date" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.processed_date}
                      minDate={minDate}
                      maxDate={maxDate}
                      onChange={(date) => handleChange("processed_date", date ?? new Date())}
                      dateFormat="dd/MM/yyyy"
                    />
                    {errors.processed_date && <h4 className="text-error">{errors.processed_date}</h4>}
                  </FormGroup>
                </Col>

                <Col md="3">
                  <CustomInput
                    name="amount"
                    label="funds_add_fund_amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    error={errors.amount}
                  >
                    {calcLoading ? (
                      <small className="text-muted d-block mt-1">Calculating share...</small>
                    ) : (
                      calculatedAmount > 0 && parseFloat(formData.amount) > 0 && parseFloat(formData.amount) < calculatedAmount && (
                        <div
                          style={{
                            backgroundColor: "#FEF3C7",
                            border: "1px solid #F59E0B",
                            borderRadius: "6px",
                            padding: "6px 10px",
                            marginTop: "6px",
                            color: "#92400E",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          <i className="fa fa-exclamation-triangle mr-1" style={{ color: "#D97706", marginRight: "4px" }}></i>
                          Entered amount is less than calculated share (2/3 Halqa Share: {calculatedAmount.toLocaleString()})
                        </div>
                      )
                    )}
                  </CustomInput>
                </Col>

                <Col md="3">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    type="text"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button
                color="white"
                className={GetCustomStyles().btn_style}
                onClick={toggle}
              >
                <GetLanguageString props="common_cancel" />
              </Button>
              <Button
                color="primary"
                className={GetCustomStyles().btn_style}
                disabled={loading}
                type="submit"
              >
                <GetLanguageString props="common_submit" />
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditHalqaShare;
